import { useState, useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { verifyMeter, verifyTransformer, getDeviceDownstream } from '../../api/topology.ts'
import { formatVerifiedBy } from '../../utils/verification.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { DownstreamButton } from './DownstreamButton.tsx'
import { MeterDetail } from './MeterDetail.tsx'
import { TransformerDetail } from './TransformerDetail.tsx'
import { FeederDetail } from './FeederDetail.tsx'
import { SubstationDetail } from './SubstationDetail.tsx'
import { SecondaryDetail } from './SecondaryDetail.tsx'
import type { GeoJSONFeatureCollection, GeoJSONPoint } from '../../types/topology.ts'

interface AssignmentMode {
  active: boolean
  meterId: number | null
}

interface DetailPanelProps {
  feature: Record<string, unknown> | null
  onClose: () => void
  onFeatureNavigate: (feature: Record<string, unknown>) => void
  assignmentMode: AssignmentMode
  onStartAssignment: (meterId: number) => void
  onCancelAssignment: () => void
  onTraceRequest: (meterId: number) => void
  traceLoading: boolean
  traceError: string | null
  onShowDownstream: (fc: GeoJSONFeatureCollection<GeoJSONPoint>) => void
  /** Report the selected meter's map location (or null) for the map's
   *  layer-independent selection highlight. */
  onDetailLocate?: (coords: { lat: number; lng: number } | null) => void
  /** Request the map pan to a point; `force` always recenters. */
  onDetailPan?: (coords: { lat: number; lng: number }, force?: boolean) => void
}

/** Right-hand detail panel for the selected map feature. */
export function DetailPanel({
  feature,
  onClose,
  onFeatureNavigate,
  assignmentMode,
  onStartAssignment,
  onCancelAssignment,
  onTraceRequest,
  traceLoading,
  traceError,
  onShowDownstream,
  onDetailLocate,
  onDetailPan,
}: DetailPanelProps) {
  if (!feature) {
    if (assignmentMode.active) {
      return (
        <div className="p-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <p className="font-medium text-blue-800">Assignment Mode</p>
            <p className="text-blue-600 mt-1">
              Click a transformer on the map to assign meter #{assignmentMode.meterId}.
            </p>
            <button
              onClick={onCancelAssignment}
              className="mt-2 px-3 py-1 text-xs bg-white border border-blue-300 rounded text-blue-700 hover:bg-blue-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="p-4 text-sm text-gray-400">
        Click a feature on the map to see details.
      </div>
    )
  }

  const featureType = detectFeatureType(feature)
  const featureId = feature.id as number | undefined
  const hasRichDetail = ['Meter', 'Transformer', 'Feeder', 'Substation'].includes(featureType) && featureId

  // Navigate to a related entity's detail view
  const handleNavigate = useCallback((type: string, id: number) => {
    onFeatureNavigate({ _type: type, id })
  }, [onFeatureNavigate])

  // Secondary line: rendered from the clicked feature's imported props + the
  // derived rooting props carried when color-by-confidence is on. No extra fetch.
  if (featureType === 'Secondary') {
    return (
      <div className="p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Secondary
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            title="Close"
          >
            &times;
          </button>
        </div>
        <SecondaryDetail feature={feature} onNavigate={handleNavigate} />
      </div>
    )
  }

  return (
    <div className="p-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          {featureType}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          title="Close"
        >
          &times;
        </button>
      </div>

      {/* Type-specific detail views */}
      {hasRichDetail ? (
        <>
          {featureType === 'Meter' && (
            <MeterDetail
              id={featureId!}
              onNavigate={handleNavigate}
              onTraceRequest={onTraceRequest}
              traceLoading={traceLoading}
              onStartAssignment={onStartAssignment}
              onLocate={onDetailLocate}
              onPan={onDetailPan}
              isSearchResult={feature._searchResult === true}
            />
          )}
          {featureType === 'Transformer' && (
            <TransformerDetail
              id={featureId!}
              onNavigate={handleNavigate}
              onShowDownstream={onShowDownstream}
            />
          )}
          {featureType === 'Feeder' && (
            <FeederDetail id={featureId!} onNavigate={handleNavigate} />
          )}
          {featureType === 'Substation' && (
            <SubstationDetail id={featureId!} onNavigate={handleNavigate} />
          )}

          {/* Verification actions for meters and transformers */}
          {(featureType === 'Meter' || featureType === 'Transformer') && (
            <VerificationActions
              featureType={featureType}
              featureId={featureId!}
              currentStatus={feature.verification_status as string | undefined}
              verifiedBy={feature.verified_by as string | null | undefined}
              verifiedAt={feature.verified_at as string | null | undefined}
            />
          )}

          {traceError && featureType === 'Meter' && (
            <p className="text-xs text-red-500 mt-2">{traceError}</p>
          )}
        </>
      ) : (
        <GenericDetail
          feature={feature}
          featureType={featureType}
          onShowDownstream={onShowDownstream}
        />
      )}
    </div>
  )
}

/** Verification action buttons for meters and transformers. */
function VerificationActions({
  featureType,
  featureId,
  currentStatus,
  verifiedBy,
  verifiedAt,
}: {
  featureType: string
  featureId: number
  currentStatus: string | undefined
  verifiedBy: string | null | undefined
  verifiedAt: string | null | undefined
}) {
  const queryClient = useQueryClient()
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  // The clicked map feature's props are a snapshot; track the status we set
  // so the buttons update immediately (spec §3.5 scope 10). Reset per feature.
  const [localStatus, setLocalStatus] = useState<string | undefined>(undefined)
  // A free-text note sent with the next verify action (item 4). Reset per feature.
  const [notes, setNotes] = useState('')
  useEffect(() => {
    setLocalStatus(undefined)
    setNotes('')
  }, [featureType, featureId])
  const effectiveStatus = localStatus ?? currentStatus

  // STALE-STAMP GUARD: the who/when line keys on the CURRENT status — a
  // downgraded record that still carries verified_by/verified_at must not read
  // as verified (formatVerifiedBy returns null unless status === 'verified').
  const verifiedLine = formatVerifiedBy(effectiveStatus, verifiedBy, verifiedAt)

  async function handleVerify(newStatus: string) {
    setVerifying(true)
    setVerifyError(null)
    const trimmed = notes.trim()
    const payload = {
      verification_status: newStatus,
      ...(trimmed ? { verification_notes: trimmed } : {}),
    }
    try {
      if (featureType === 'Meter') {
        await verifyMeter(featureId, payload)
        await queryClient.invalidateQueries({ queryKey: ['meter', featureId] })
      } else {
        await verifyTransformer(featureId, payload)
        await queryClient.invalidateQueries({ queryKey: ['transformer', featureId] })
      }
      await queryClient.invalidateQueries({ queryKey: ['map'] })
      await queryClient.invalidateQueries({ queryKey: ['topology-stats'] })
      await queryClient.invalidateQueries({ queryKey: ['data-quality'] })
      // A single verify/flag also shifts the feeder's verification bars —
      // refresh any mounted VerificationProgressCard.
      await queryClient.invalidateQueries({ queryKey: ['feeder-verification'] })
      setLocalStatus(newStatus)
    } catch (err) {
      setVerifyError(friendlyError(err, 'save the verification'))
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Verification</p>
      {verifiedLine && (
        <p className="text-[11px] text-green-700">{verifiedLine}</p>
      )}
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Add a note (sent with the next verify action)…"
        rows={2}
        className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
      <div className="flex flex-wrap gap-1.5">
        {effectiveStatus !== 'verified' && (
          <ActionButton
            onClick={() => handleVerify('verified')}
            disabled={verifying}
            className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
          >
            Verify
          </ActionButton>
        )}
        {effectiveStatus !== 'flagged' && (
          <ActionButton
            onClick={() => handleVerify('flagged')}
            disabled={verifying}
            className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
          >
            Flag
          </ActionButton>
        )}
        {effectiveStatus !== 'needs_review' && (
          <ActionButton
            onClick={() => handleVerify('needs_review')}
            disabled={verifying}
            className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          >
            Needs Review
          </ActionButton>
        )}
        {effectiveStatus !== 'unverified' && (
          <ActionButton
            onClick={() => handleVerify('unverified')}
            disabled={verifying}
            className="bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
          >
            Reset
          </ActionButton>
        )}
      </div>
      {verifying && <p className="text-xs text-gray-400">Updating...</p>}
      {verifyError && <p className="text-xs text-red-500">{verifyError}</p>}
    </div>
  )
}

/** Generic property table for conductors, devices, and unrecognized features. */
function GenericDetail({
  feature,
  featureType,
  onShowDownstream,
}: {
  feature: Record<string, unknown>
  featureType: string
  onShowDownstream: (fc: GeoJSONFeatureCollection<GeoJSONPoint>) => void
}) {
  const title = (feature.name ?? feature.gis_id ?? `${featureType} #${feature.id}`) as string
  const hiddenKeys = new Set([
    'geometry',
    'type',
    '_searchResult',
    '_type',
    '_geometry',
    'has_normally_open_way',
  ])
  const deviceId = featureType === 'Device' ? (feature.id as number | undefined) : undefined

  return (
    <>
      <div className="text-base font-semibold text-gray-900 mb-2 truncate" title={title}>
        {title}
      </div>
      {deviceId != null && (
        <DownstreamButton
          // Keyed by device so a device -> device switch resets count/notice
          // state instead of briefly showing the prior device's result.
          key={deviceId}
          label="Show everything fed by this device"
          fetcher={() => getDeviceDownstream(deviceId)}
          onShowDownstream={onShowDownstream}
        />
      )}
      {feature.has_normally_open_way === true && (
        <div className="mb-3">
          <span
            className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
            title="A normally-open tie point is recorded at this device. The open way is folded into this device, so it leads chains instead of the tie point."
          >
            Has a normally-open way
          </span>
        </div>
      )}
      {typeof feature.verification_status === 'string' && (
        <div className="mb-3">
          <StatusBadge status={feature.verification_status} />
        </div>
      )}
      <div className="space-y-1.5 mb-4">
        {Object.entries(feature)
          .filter(([key]) => !hiddenKeys.has(key))
          .map(([key, value]) => (
            <div key={key} className="flex justify-between text-sm">
              <span className="text-gray-500 truncate mr-2">{formatKey(key)}</span>
              <span className="text-gray-800 font-medium text-right truncate max-w-[60%]">
                {formatValue(value)}
              </span>
            </div>
          ))}
      </div>
    </>
  )
}

function ActionButton({
  onClick,
  disabled,
  className,
  children,
}: {
  onClick: () => void
  disabled: boolean
  className: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-2.5 py-1 text-xs font-medium border rounded transition-colors disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  )
}

export function detectFeatureType(props: Record<string, unknown>): string {
  // Explicit type from navigation or search
  const explicitType = props._type as string | undefined
  if (explicitType) return explicitType

  // Infer from properties
  if ('meter_number' in props) return 'Meter'
  if ('kva_rating' in props) return 'Transformer'
  if ('voltage_kv' in props && 'substation_id' in props) return 'Feeder'
  if ('voltage_primary_kv' in props) return 'Substation'
  if ('segment_type' in props) return 'Conductor'
  if ('device_type' in props) return 'Device'
  return 'Feature'
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '--'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    verified: 'bg-green-100 text-green-800',
    unverified: 'bg-yellow-100 text-yellow-800',
    flagged: 'bg-red-100 text-red-800',
    needs_review: 'bg-purple-100 text-purple-800',
  }

  return (
    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}
